import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { SI_ACADEMY_URL, SI_PRICING_URL, SI_STATUS_URL, SI_SUPPORT_HC_URL, SI_SUPPORT_URL } from '../../../core/config';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UserService } from '../../../core/services/user.service';
import { User } from '../../../core/models/user.model';
import { CompanyService } from '../../../results/companies/services/companies.service';
import { ContactsService } from '../../../results/contacts/services/contacts.service';
import { AccountApiService } from '../../services/api';
import { LoadingService } from '../../services/loading.service';
import { RequestBodyService } from '../../services/request-body.service';
import { CommonUtilService } from '../../services/CommonUtil.service';
import { SavedSearches } from '../../services/saved-searches.service';
import { ValidationService } from '../../services/validation.service';
import { buildTrainingRequestUrl } from '../../helpers/training-request-url.helper';
import { AccountAttributesEnum, FeatureFlagsEnum, FavoritePageEnum } from '../../enums';
import { getNavIconForPage, ModalNavigationEnum, PageTitles } from '../../constants';
import { HasAccessPipeModule } from '../../pipes/has-access/has-access-pipe.module';
import { UserMetaDataByKeyPipeModule } from '../../pipes/user-meta-data-by-key/user-meta-data-by-key.module';
import { OverlayDropdownComponent } from '../overlay-dropdown/overlay-dropdown.component';
import { DropdownPanelDirective, DropdownTriggerDirective } from '../overlay-dropdown/overlay-dropdown.directive';
import { OverlayLoaderComponent } from '../../overlay-loader/overlay-loader.component';
import { ShowRightHeaderContainerPipe } from './pipes/show-right-header-container.pipe';
import { ExpiredCreditsTooltipPipe } from './pipes/expired-credits-tooltip.pipe';

interface HeaderPageIdentifier {
  title: string;
  page: FavoritePageEnum | null;
  sectionIcon: IconProp | null;
}

const LOADING_TAG: string = 'favorite-loading-tag';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    FontAwesomeModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    HasAccessPipeModule,
    UserMetaDataByKeyPipeModule,
    OverlayDropdownComponent,
    DropdownTriggerDirective,
    DropdownPanelDirective,
    OverlayLoaderComponent,
    ShowRightHeaderContainerPipe,
    ExpiredCreditsTooltipPipe,
  ],
})
export class HeaderComponent implements OnInit {
  isLoggedIn: boolean = false;
  unlimitedExports: boolean = false;
  trialDaysLeftLabel: string = '';
  hasCredit: boolean = false;
  logOutTrue: boolean = true;
  hasRODCredit: boolean = false;
  creditLimitnRemaining: string = '';
  creditRevealLimitnRemaining: string = '';
  loggedInUserData: User;
  userInitials: string = '';
  userFullName: string = '';
  expiredCredits: boolean = false;
  isRevdriver: boolean = false;
  isimpersonateHaveValue: boolean = false;
  impersonateForm: FormGroup;
  licenceCredit: number = 0;
  licenceCreditTotal: number = 0;
  sharedCredit: number = 0;
  sharedCreditTotal: number = 0;
  accountType: number;
  trainingRequestUrl: string = buildTrainingRequestUrl();

  isImpersonate: boolean = false;
  isActivationPage: boolean = false;

  pageIdentifier$: Observable<HeaderPageIdentifier>;
  readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);

  readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;
  readonly modalNavigationEnum: typeof ModalNavigationEnum = ModalNavigationEnum;
  readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;

  readonly pricingUrl: string = SI_PRICING_URL;
  readonly supportUrl: string = SI_SUPPORT_URL;
  readonly supportHelpCenterUrl: string = SI_SUPPORT_HC_URL;
  readonly academyUrl: string = SI_ACADEMY_URL;
  readonly statusUrl: string = SI_STATUS_URL;

  constructor(
    public readonly router: Router,
    public readonly authenticationService: AuthenticationService,
    public readonly savedSearches: SavedSearches,
    public readonly companyService: CompanyService,
    public readonly contactsService: ContactsService,
    public readonly requestBodyService: RequestBodyService,
    public readonly commonUtilService: CommonUtilService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly accountApiService: AccountApiService,
    private readonly loadingService: LoadingService,
  ) {
    this.pageIdentifier$ = this.router.events.pipe(
      startWith(new NavigationEnd(0, this.router.url, this.router.url)),
      filter((event) => event instanceof NavigationEnd),
      map(() => {
        let current = this.activatedRoute.root;
        while (current?.firstChild && !current?.snapshot?.data?.pageIdentifier) {
          current = current?.firstChild;
        }
        const page: FavoritePageEnum | null = (current?.snapshot?.data?.pageIdentifier as FavoritePageEnum | undefined) ?? null;
        const title: string = page ? (PageTitles[page]?.trim() ?? '') : '';
        const sectionIcon: IconProp | null = getNavIconForPage(page);

        return { title, page, sectionIcon };
      }),
    );

    // TODO: review this logic
    router.events.pipe(untilDestroyed(this)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.isActivationPage = e.url.includes('account/verification/');
        if (e.url && e.url.includes('resetPassword')) {
          this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
            this.isRevdriver = params['from'] === 'revdriver';
          });
        } else if (e.url.includes('revdriver-steps') || e.url.includes('outreach')) {
          this.isRevdriver = true;
        }
      }
    });
  }

  ngOnInit(): void {
    this.impersonateForm = this.formBuilder.group({
      impersonatedEmail: ['', Validators.compose([Validators.required, ValidationService.emailValidator])],
    });

    this.getUserData();
  }

  onLogin(): void {
    this.router.navigate(['account']);
  }

  onOpenRodModal(): void {
    this.commonUtilService.openRoDModal();
  }

  onImpersonate(): void {
    const impersonateValue: string = this.impersonateForm.value.impersonatedEmail;
    if (!this.impersonateForm.valid) return;

    if (this.isimpersonateHaveValue) {
      this.isimpersonateHaveValue = false;
      this.impersonateForm.get('impersonatedEmail').setValue('');
      this.authenticationService.removeImpersonatedEmail();
    } else {
      this.isimpersonateHaveValue = true;
      this.authenticationService.setImpersonatedEmail(impersonateValue);
    }
    window.location.reload();
  }

  toggleFavorite(favoritePage: FavoritePageEnum, currentPage: FavoritePageEnum): void {
    if (favoritePage === currentPage) return;

    const newFavoritePage: { favoritePage: FavoritePageEnum } = { favoritePage: currentPage };
    this.setFavoritePage(newFavoritePage).pipe(
      switchMap(() => this.loadingService.loadingWrapper(this.authenticationService.updateLoggedUserData(), LOADING_TAG)),
      untilDestroyed(this),
    ).subscribe();
  }

  private getUserData(): void {
    // TODO: review logic
    this.userService.getUserData().pipe(untilDestroyed(this)).subscribe((data) => {
      this.accountType = data?.account_type;
      this.loggedInUserData = data;
      this.userInitials = this.getUserInitials(data);
      this.userFullName = this.getUserFullName(data);
      this.trainingRequestUrl = buildTrainingRequestUrl(data);

      if (data) {
        this.isLoggedIn = true;
      } else {
        this.isLoggedIn = false;
        this.savedSearches.currentSearchId = '';
        this.savedSearches.currentSearchIdVisitorIntel = '';
        this.companyService.resultPage.companySidebarData = null;
        this.companyService.resultPage.IsNavigating = false;
        this.contactsService.resultPage.contactSidebarData = null;
        this.contactsService.resultPage.IsNavigating = false;
      }

      const impersonatedEmail: string = this.authenticationService.getImpersonatedEmail();
      if (impersonatedEmail) {
        this.isimpersonateHaveValue = true;
        this.impersonateForm.get('impersonatedEmail').setValue(impersonatedEmail);
      } else {
        this.isimpersonateHaveValue = false;
        this.impersonateForm.get('impersonatedEmail').setValue('');
      }

      this.isImpersonate = !!this.loggedInUserData?.roles?.includes('ROLE_USER_impersonate');

      if (!this.loggedInUserData) return;

      this.unlimitedExports = this.userService.hasAccess(AccountAttributesEnum.UNLIMITED_EXPORTS);
      this.expiredCredits = new Date(this.loggedInUserData.billing_period_end) < new Date();

      /**
       * Logic to check Combined Type and RoD credit
       *
       * If user doesn't have any type credit type then it will assign false to 'hasCredit' as well as to 'hasRODCredit'
       */
      if (Array.isArray(this.loggedInUserData.service_credits) && this.loggedInUserData.service_credits.length > 0) {
        this.hasCredit = this.commonUtilService.getCombinedRemainingCredits() > 0;
      }
      this.logOutTrue = this.hasCredit;

      // Logic to trial Account
      if (this.userService.hasAccess(AccountAttributesEnum.TRIAL)) {
        const trialDaysLeft: number | null = this.getDaysUntilDate(this.loggedInUserData.billing_period_end);
        if (trialDaysLeft === null) {
          this.trialDaysLeftLabel = '';
        } else if (trialDaysLeft < 0) {
          this.trialDaysLeftLabel = 'Expired';
        } else {
          this.trialDaysLeftLabel = this.formatDaysLeft(trialDaysLeft);
        }
      } else {
        this.trialDaysLeftLabel = '';
      }

      this.creditLimitnRemaining = '';
      // Logic to unlimited case
      if (this.unlimitedExports) {
        this.creditLimitnRemaining = 'Unlimited';
      } else if (this.loggedInUserData.service_credits?.length) {
        // Logic to non-trial
        const combinedCredit = this.loggedInUserData.service_credits.find((creditData) => creditData.credit_type === 'Combined');
        if (combinedCredit) {
          this.licenceCredit = combinedCredit.license_credit_limit - combinedCredit.license_credits_used;
          this.licenceCreditTotal = combinedCredit.license_credit_limit;
          this.sharedCredit = combinedCredit.credits_remaining;
          this.sharedCreditTotal = combinedCredit.credit_limit;
          this.creditLimitnRemaining = this.commonUtilService.getCombinedRemainingCredits().toLocaleString();
        }

        // Logic for reveal credits
        const revealCredits = this.loggedInUserData.service_credits.find((creditData) => creditData.credit_type === 'Reveal');
        if (revealCredits) {
          this.creditRevealLimitnRemaining = revealCredits.credits_remaining < 0
            ? `${revealCredits.total_credit_limit.toLocaleString()} / ${revealCredits.total_credit_limit.toLocaleString()}`
            : `${revealCredits.credits_used.toLocaleString()} / ${revealCredits.total_credit_limit.toLocaleString()}`;
        }
      }
    });
  }

  private setFavoritePage(favoritePage: { favoritePage: FavoritePageEnum }): Observable<unknown> {
    return this.loadingService.loadingWrapper(
      this.accountApiService.setFavoritePage(this.loggedInUserData.id, favoritePage),
      LOADING_TAG,
    );
  }

  private getUserFullName(user: User | null | undefined): string {
    const firstName: string = user?.first_name?.trim() ?? '';
    const lastName: string = user?.last_name?.trim() ?? '';
    return [firstName, lastName].filter((part: string) => part.length > 0).join(' ');
  }

  private getUserInitials(user: User | null | undefined): string {
    const firstName: string = user?.first_name?.trim() ?? '';
    const lastName: string = user?.last_name?.trim() ?? '';

    const firstInitial: string = firstName ? firstName.charAt(0) : '';
    const lastInitial: string = lastName ? lastName.charAt(0) : '';
    const initials: string = `${firstInitial}${lastInitial}`.trim();
    if (initials) return initials.toUpperCase();

    const emailInitial: string = user?.email?.trim() ? user.email.charAt(0) : '';
    return emailInitial ? emailInitial.toUpperCase() : '';
  }

  private getDaysUntilDate(dateIso: string | null | undefined): number | null {
    if (!dateIso) return null;

    const datePart: string = dateIso.split('T')[0] ?? '';
    const [year, month, day] = datePart.split('-').map((part: string) => Number(part));
    if (!year || !month || !day) return null;

    const endUtc: number = Date.UTC(year, month - 1, day);
    const now: Date = new Date();
    const todayUtc: number = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

    return Math.floor((endUtc - todayUtc) / (1000 * 60 * 60 * 24));
  }

  private formatDaysLeft(daysLeft: number): string {
    return daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
  }
}
